import React from 'react';
import { CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { BarChart, Bar } from 'recharts';
import { LineChart, Line } from 'recharts';
import { Grid, Row, Col } from '../ui-kit';
import moment from 'moment';

const AlertingPerformance = ({ statsPerWeek }) => {
  const validIncidentStates = ['alerting', 'failed', 'finished'];
  const validPnStates = ['sent', 'delivered'];
  const incidentStateColors = { alerting: '#33cc33', failed: '#cc0000', finished: '#003399' };
  const pnStateColors = { sent: '#cc9900', delivered: '#33cc33' };
  const pnLatencyCalculations = ['p50', 'p90', 'p95'];
  const pnLatencyLineColors = {
    p50: '#002266',
    p90: '#004de6',
    p95: '#b3ccff'
  };

  // Data initializers
  const incidentsPerStatusData = [];
  const pnsPerStatusData = [];
  const pnSendingLatencyData = [];
  const pnDeliveryLatencyData = [];
  const incidentsCompleteCrewTimeData = [];

  for (const timestamp of Object.keys(statsPerWeek)) {
    const currentStats = statsPerWeek[timestamp];
    const name = moment(timestamp).format('DD-MM-YYYY');

    // Prepare data for incidents per status chart
    const incidentCounts = currentStats.incident_count_by_status;
    let totalIncidents = 0;
    for (const value of Object.values(incidentCounts)) totalIncidents += value;
    incidentsPerStatusData.push({
      name,
      ...incidentCounts,
      total: totalIncidents
    });

    // Prepare data for push notifications per status
    const pnCounts = currentStats.push_notification_count_by_status;
    let totalPns = 0;
    for (const value of Object.values(pnCounts)) totalPns += value;
    pnsPerStatusData.push({
      name,
      ...pnCounts,
      total: totalPns
    });

    // Prepare data for push notification sent latencies
    const pnSendingLatencies = currentStats.push_notification_sending_latency_stats;
    pnSendingLatencyData.push({
      name,
      ...pnSendingLatencies
    });

    // Prepare data for push notification delivery latencies
    const pnDeliveryLatencies = currentStats.push_notification_delivery_latency_stats;
    pnDeliveryLatencyData.push({
      name,
      ...pnDeliveryLatencies
    });

    // Prepare data for complete crew time
    const incidentsCompleteCrewTimes = currentStats.incident_complete_crew_time_stats;
    incidentsCompleteCrewTimeData.push({
      name,
      ...incidentsCompleteCrewTimes
    });
  }

  const generateBarChart = (header, xAxisKey, dataSet, values, styles) => {
    return (
      <Col xs={12} sm={12} md={12} lg={6}>
        <h4>{header}</h4>
        <BarChart
          width={600}
          height={400}
          data={dataSet}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} />
          <YAxis label={{ value: 'total', angle: -90, position: 'insideLeft' }} />
          <Tooltip />
          <Legend />
          {values.map(value => (
            <Bar key={value} stackId="a" dataKey={value} fill={styles[value]} />
          ))}
        </BarChart>
      </Col>
    );
  };

  const generateLineChart = (header, xAxisKey, dataSet, values, styles) => {
    return (
      <Col xs={12} sm={12} md={6} lg={6}>
        <h4>{header}</h4>
        <LineChart
          width={600}
          height={400}
          data={dataSet}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} />
          <YAxis label={{ value: 'seconds', angle: -90, position: 'insideLeft' }} />
          <Tooltip />
          <Legend />
          {values.map(value => (
            <Line key={value} type="monotone" dataKey={value} stroke={styles[value]} />
          ))}
        </LineChart>
      </Col>
    );
  };

  return (
    <React.Fragment>
      <Grid>
        <Row>
          {generateLineChart(
            I18n.t('reports.group_reports.alerting_performance.incident_complete_crew_time_stats'),
            'name',
            incidentsCompleteCrewTimeData,
            pnLatencyCalculations,
            pnLatencyLineColors
          )}
          {generateBarChart(
            I18n.t('reports.group_reports.alerting_performance.incident_count_by_status'),
            'name',
            incidentsPerStatusData,
            validIncidentStates,
            incidentStateColors
          )}
        </Row>
        <Row>
          {generateBarChart(
            I18n.t('reports.group_reports.alerting_performance.push_notification_count_by_status'),
            'name',
            pnsPerStatusData,
            validPnStates,
            pnStateColors
          )}
        </Row>
        <Row>
          {generateLineChart(
            I18n.t('reports.group_reports.alerting_performance.push_notification_sending_latency_stats'),
            'name',
            pnSendingLatencyData,
            pnLatencyCalculations,
            pnLatencyLineColors
          )}
          {generateLineChart(
            I18n.t('reports.group_reports.alerting_performance.push_notification_delivery_latency_stats'),
            'name',
            pnDeliveryLatencyData,
            pnLatencyCalculations,
            pnLatencyLineColors
          )}
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default AlertingPerformance;
